import { hpBasis } from '../matchup';
import { DEFENSE_SLOTS, OFFENSE_SLOTS } from '../playdef';
import { charted, itemOf, type Side } from '../subs';
import type { BagUse, Combatant, Game, Sub, SubWindow } from '../types';
import { atSlot, lineOf, maxWinChance, opponentOf, sideWinChance } from './board';
import { lineups, noise, teamOn, type AiContext } from './context';

/**
 * The AI's sub window (slice 7), from the board. For each slot of the chart in play:
 *
 *   value(x, slot) = x's win chance on the slot's board line against the Pokémon across from it
 *                    (position fit, fatigue, status; a bench Pokémon's stages clear on the way in)
 *   gain           = value(bench Pokémon) - value(starter)
 *   sub when       gain >= coach.ai.subs.minGain + noise   (a better matchup; not Rookie)
 *              or  the starter is under its level's restBelow and gain > 0   (a healthier fit)
 *
 * Greedy by largest gain, each bench Pokémon at most once. Then the bag: one use a window, on a
 * tired starter no bench Pokémon beats (coach.ai.bag). Draws noise only (one draw a slot).
 */

export interface SubCandidate {
  slot: string;
  in: number;
  gain: number;
  /** The starter is under the fatigue threshold. */
  tired: boolean;
}

const share = (c: Combatant) => c.hp / c.stats.hp;

/** The window a side's AI takes before this call's snap, given the game as it sees it. */
export function planWindow(game: Readonly<Game>, side: Side, ctx: AiContext): SubWindow<string> {
  const b = ctx.b;
  const teamId = teamOn(game, side);
  const team = game.teams[teamId];
  const chart = team[side] as Record<string, number>;
  const slots: readonly string[] = side === 'offense' ? OFFENSE_SLOTS : DEFENSE_SLOTS;
  const { theirs } = lineups(game, side);
  const onField = charted(team);
  const bench = team.roster
    .map((c, i) => ({ c, i }))
    .filter(({ c, i }) => !onField.has(i) && !(c.sitPlays ?? 0) && c.hp > 0);

  // The level's look ahead: both are scored as they would stand after `ahead` more snaps of
  // play, so a starter about to tire goes out while a healthier fit is still the better bet.
  const level = b.coach.difficulty[ctx.difficulty];
  const ahead = level.lookahead;
  const loss = ahead * b.coach.ai.subs.lossPerSnap;
  const later = (c: Combatant): Combatant =>
    ahead > 0 && c.hp > 0
      ? { ...c, hp: Math.max(0, c.hp - Math.round(loss * hpBasis(c.stats.hp, b))) }
      : c;
  const ready = bench.map(({ c, i }) => ({ c: later(c), i }));
  const best = maxWinChance(b);
  /** `c` (already looked ahead) in `slot` against the Pokémon across from it. */
  const value = (c: Combatant, slot: string, fromBench: boolean) => {
    const s = slot as Parameters<typeof lineOf>[0];
    return sideWinChance(
      side,
      lineOf(s).kind,
      atSlot(c, s, fromBench),
      theirs[opponentOf(s)] as Combatant,
      b,
    );
  };

  const tired = new Set<string>();
  const pairs: SubCandidate[] = [];
  for (const slot of slots) {
    const starter = team.roster[chart[slot] as number] as Combatant;
    const base = value(later(starter), slot, false);
    const isTired = starter.hp <= 0 || share(starter) < level.restBelow;
    if (isTired) tired.add(slot);
    const bar = b.coach.ai.subs.minGain + noise(ctx, b.coach.ai.jitterScale.sub);
    // A starter already sure to win (but for the crit) and not tired cannot be bettered.
    if (!isTired && base >= best) continue;
    for (const { c, i } of ready) {
      const gain = value(c, slot, true) - base;
      if (isTired && gain > 0) pairs.push({ slot, in: i, gain, tired: true });
      else if (level.matchupSubs && gain >= bar) pairs.push({ slot, in: i, gain, tired: false });
    }
  }
  // Tired starters first, then the biggest gains. A Pokémon who still plays the other way does not
  // reach the bench when he goes out, so a matchup sub that would leave fewer than the level's
  // keepBench on the bench is passed up: the bench is what lets tired starters rest.
  pairs.sort(
    (x, y) =>
      Number(y.tired) - Number(x.tired) ||
      y.gain - x.gain ||
      slots.indexOf(x.slot) - slots.indexOf(y.slot) ||
      x.in - y.in,
  );
  const otherChart = new Set(Object.values(team[side === 'offense' ? 'defense' : 'offense']));
  const keep = level.keepBench;
  let benchLeft = bench.length;
  const filled = new Set<string>();
  const used = new Set<number>();
  const subs: Sub<string>[] = [];
  for (const p of pairs) {
    if (filled.has(p.slot) || used.has(p.in)) continue;
    const after = benchLeft - (otherChart.has(chart[p.slot] as number) ? 1 : 0);
    if (!p.tired && after < keep) continue;
    benchLeft = after;
    filled.add(p.slot);
    used.add(p.in);
    subs.push({ slot: p.slot, in: p.in });
  }
  subs.sort((x, y) => slots.indexOf(x.slot) - slots.indexOf(y.slot));

  const bag = bagUse(
    team.bag ?? [],
    slots.filter((s) => !filled.has(s)).map((s) => ({ slot: s, i: chart[s] as number })),
    team.roster,
    tired,
    ctx,
  );
  return {
    ...(subs.length > 0 ? { subs } : {}),
    ...(bag ? { bag: [bag] } : {}),
  };
}

/**
 * One bag use: the heal item on the most tired starter that stays in (the biggest item when it is
 * down coach.ai.bag.bigHealAt HP or more, else the smallest), else the cure on a starter with a
 * lasting paralysis or burn.
 */
function bagUse(
  bag: readonly string[],
  staying: { slot: string; i: number }[],
  roster: readonly Combatant[],
  tired: ReadonlySet<string>,
  ctx: AiContext,
): BagUse | undefined {
  const b = ctx.b;
  if (bag.length === 0) return undefined;
  const heals = [...new Set(bag)]
    .map((id) => ({ id, fx: itemOf(id, b)?.effect }))
    .flatMap((x) => (x.fx?.kind === 'heal-hp' ? [{ id: x.id, hp: x.fx.hp }] : []))
    .sort((x, y) => x.hp - y.hp || (x.id < y.id ? -1 : 1));
  const worn = staying
    .filter((s) => tired.has(s.slot))
    .map((s) => ({ ...s, c: roster[s.i] as Combatant }))
    .sort((x, y) => share(x.c) - share(y.c) || x.i - y.i);
  const target = worn[0];
  if (target && heals.length > 0) {
    const deficit = target.c.stats.hp - target.c.hp;
    const item = deficit >= b.coach.ai.bag.bigHealAt ? heals.at(-1) : heals[0];
    if (item) return { item: item.id, target: target.i };
  }
  const cure = bag.find((id) => itemOf(id, b)?.effect.kind === 'cure-status');
  if (!cure) return undefined;
  const statuses: readonly string[] = b.coach.ai.bag.cureStatuses;
  const sick = staying.find(({ i }) => {
    const st = (roster[i] as Combatant).status;
    return (
      st !== undefined && statuses.includes(st.kind) && st.playsLeft >= b.coach.ai.bag.cureMinPlays
    );
  });
  return sick ? { item: cure, target: sick.i } : undefined;
}
